import { Banner, Text } from '@lmc/design-system'
import { InjuredPersonTypeEnum } from '@lmc/validations'
import { Form as AntdForm, AutoComplete, Checkbox, Input, Select } from 'antd'
import { NAMESPACE } from 'locale'
import { incidentReportsConstants } from 'locale/incidentReports'
import { useTranslation } from 'react-i18next'
import RelatedBodyMapsInput from '../../../bodyMaps/RelatedBodyMapsInput'
import StepDrawer from '../../StepDrawer'
import StepField from '../../StepField'
import StepFieldLabel from '../../StepFieldLabel'
import { StepReveal } from '../../StepReveal'
import type { StepOption } from '../../stepOptions'
import type {
  InjuredPeopleOptions,
  InjuryEntryValue
} from './injuryEntryValues'

type InjuryDrawerProps = {
  entry: InjuryEntryValue
  isNew: boolean
  nominative: string
  onClose: () => void
  onConfirm: (entry: InjuryEntryValue) => void
  /** Who can be picked: those not already recorded, plus this entry's person. */
  people: InjuredPeopleOptions
  serviceUser: { id: string; name: string }
  showBodyMaps: boolean
  teamMemberOptions: Array<StepOption>
  testId: string
}

const TITLE_KEYS: Record<InjuredPersonTypeEnum, string> = {
  [InjuredPersonTypeEnum.otherParty]:
    incidentReportsConstants.CASE_V2_INJURIES_ADDING_OTHER_PARTY,
  [InjuredPersonTypeEnum.otherServiceUser]:
    incidentReportsConstants.CASE_V2_INJURIES_ADDING_OTHER_SERVICE_USER,
  [InjuredPersonTypeEnum.serviceUser]:
    incidentReportsConstants.CASE_V2_INJURIES_ADDING_SERVICE_USER_INJURY,
  [InjuredPersonTypeEnum.teamMember]:
    incidentReportsConstants.CASE_V2_INJURIES_ADDING_TEAM_MEMBER
}

/**
 * Adds or edits one person's injury: who, the case's own service user's body
 * maps, what happened, and any treatment given.
 */
const InjuryDrawer = ({
  entry,
  isNew,
  nominative,
  onClose,
  onConfirm,
  people,
  serviceUser,
  showBodyMaps,
  teamMemberOptions,
  testId
}: InjuryDrawerProps) => {
  const { t } = useTranslation(NAMESPACE.INCIDENT_REPORTS)
  const [form] = AntdForm.useForm<InjuryEntryValue>()
  const treatmentGiven = AntdForm.useWatch('treatmentGiven', form)
  const { personType } = entry
  const placeholder = t(incidentReportsConstants.CASE_V2_SELECT_PLACEHOLDER)
  const nameLabel = t(incidentReportsConstants.CASE_V2_INJURIES_NAME)

  return (
    <StepDrawer
      form={form}
      initialValues={entry}
      onCancel={onClose}
      onConfirm={onConfirm}
      testId={testId}
      title={
        isNew
          ? t(TITLE_KEYS[personType], { variable: nominative })
          : t(incidentReportsConstants.CASE_V2_INJURIES_EDITING_INJURY)
      }
      confirmLabel={t(
        isNew
          ? incidentReportsConstants.CASE_V2_INJURIES_ADD_INJURY
          : incidentReportsConstants.CASE_V2_SAVE
      )}
    >
      {personType === InjuredPersonTypeEnum.serviceUser && (
        <div>
          <StepFieldLabel label={nameLabel} isRequired />
          <Text tone="secondary" data-testid={`${testId}-service-user`}>
            {serviceUser.name}
          </Text>
        </div>
      )}
      {personType === InjuredPersonTypeEnum.otherServiceUser && (
        <Banner
          data-testid={`${testId}-separate-log-banner`}
          title={t(
            incidentReportsConstants.CASE_V2_INJURIES_OTHER_SERVICE_USER_BANNER_TITLE
          )}
          type="info"
        >
          {t(
            incidentReportsConstants.CASE_V2_INJURIES_OTHER_SERVICE_USER_BANNER_BODY,
            { variable: nominative }
          )}
        </Banner>
      )}
      {personType === InjuredPersonTypeEnum.otherServiceUser && (
        <StepField name="residentId" isRequired label={nameLabel}>
          <Select
            data-testid={`${testId}-resident`}
            options={people.otherServiceUsers}
            placeholder={placeholder}
            showSearch
            optionFilterProp="label"
          />
        </StepField>
      )}
      {personType === InjuredPersonTypeEnum.teamMember && (
        <StepField name="userId" isRequired label={nameLabel}>
          <Select
            data-testid={`${testId}-team-member`}
            options={people.teamMembers}
            placeholder={placeholder}
            showSearch
            optionFilterProp="label"
          />
        </StepField>
      )}
      {personType === InjuredPersonTypeEnum.otherParty && (
        <StepField name="name" isRequired label={nameLabel}>
          <AutoComplete
            data-testid={`${testId}-name`}
            options={people.otherParties.map((name) => ({ value: name }))}
            filterOption={(input, option) =>
              (option?.value ?? '').toLowerCase().includes(input.toLowerCase())
            }
          />
        </StepField>
      )}
      {personType === InjuredPersonTypeEnum.serviceUser && showBodyMaps && (
        <StepField
          name="bodyMaps"
          label={t(incidentReportsConstants.CASE_V2_INJURIES_RELATED_BODY_MAPS)}
          hint={t(
            incidentReportsConstants.CASE_V2_INJURIES_RELATED_BODY_MAPS_HINT
          )}
        >
          <RelatedBodyMapsInput
            isInStepDrawer
            residentId={serviceUser.id}
            testId={`${testId}-body-maps`}
          />
        </StepField>
      )}
      <StepField
        name="details"
        label={t(incidentReportsConstants.CASE_V2_INJURIES_DETAILS)}
        hint={t(incidentReportsConstants.CASE_V2_INJURIES_INJURY_DETAILS_HINT)}
      >
        <Input.TextArea
          data-testid={`${testId}-details`}
          autoSize={{ minRows: 3 }}
        />
      </StepField>
      <AntdForm.Item name="treatmentGiven" valuePropName="checked" noStyle>
        <Checkbox data-testid={`${testId}-treatment-given`}>
          {t(incidentReportsConstants.CASE_V2_INJURIES_TREATMENT_GIVEN)}
        </Checkbox>
      </AntdForm.Item>
      {treatmentGiven && (
        <StepReveal>
          <StepField
            name="treatmentGivenBy"
            isRequired
            label={t(incidentReportsConstants.CASE_V2_INJURIES_BY_WHOM)}
          >
            <Select
              data-testid={`${testId}-treatment-given-by`}
              options={teamMemberOptions}
              placeholder={placeholder}
              showSearch
              optionFilterProp="label"
            />
          </StepField>
          <StepField
            name="treatment"
            isRequired
            label={t(incidentReportsConstants.CASE_V2_INJURIES_TREATMENT)}
            hint={t(incidentReportsConstants.CASE_V2_INJURIES_TREATMENT_HINT)}
          >
            <Input data-testid={`${testId}-treatment`} />
          </StepField>
        </StepReveal>
      )}
    </StepDrawer>
  )
}

export default InjuryDrawer
